import React, {useEffect, useState, useMemo} from 'react';
import { Typography, Select, Button, Divider, Modal, Input, message } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { getDemandDaysAction } from '../../../actions/demand-days.action';
import { demandDaysSelector } from '../../../selectors/demand-days-selectors';
import { ScheduleListCalender } from '../render-calender/render-calender.component';
import { currentDate, firstDayOfMonth, lastDayOfMonth, addMonth } from '../../../utils/dates-util';
import DemandDaysTable from '../demand-days-table/demand-days-table';
import { getMasterCities } from '../../../actions/locations-actions';
import { locationReducerSelector } from '../../../selectors/location-selectors';
import { updateDemandDay } from '../demand-days-table/demand-days-table.module';

const {Text} = Typography;
const {TextArea} = Input;

const DemandDaysRoot = () => {

    const dispatch = useDispatch();
    const demandDaysData = useSelector(demandDaysSelector);
    const {
        masterCities 
    } = useSelector(locationReducerSelector);
    const [selectedOrigin, setSelectedOrigin] = useState([]);
    const [selectedDestination, setSelectedDestination] = useState([]);
    const [isDisabled, setIsDisabled] = useState(true);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [editDescription, setEditDescription] = useState('');
    const scheduleDate = currentDate();


    useEffect(() => {
        dispatch(getMasterCities());
    },[])

    // Memoize city options to prevent unnecessary re-renders
    const cityOptions = useMemo(() => {
        if (!masterCities || !Array.isArray(masterCities)) {
            return [];
        }
        const cities = masterCities.map(city => ({
            label: city.city_name,
            value: city.city_name
        }));
        // Add "All" option at the top
        return [{ label: 'All', value: 'all' }, ...cities];
    }, [masterCities]);

    // Get all city values (excluding 'all')
    const allCityValues = useMemo(() => {
        return cityOptions.filter(opt => opt.value !== 'all').map(opt => opt.value);
    }, [cityOptions]);
    
    const handleSelect = (value, type) => {
        let finalValue = value;
        
        if (type=="origin") {
            setSelectedOrigin(finalValue);
            if (selectedDestination.length>0&&finalValue.length>0) {
                setIsDisabled(false);
            }
            else {
                setIsDisabled(true);
            }
        }
        else {
            setSelectedDestination(finalValue);
            if (selectedOrigin.length>0&&finalValue.length>0) {
                setIsDisabled(false);
            }
            else {
                setIsDisabled(true);
            }
        }
    };

    const fetchDemandDays= () => {
        const today = currentDate();
        const startDate = firstDayOfMonth(today);
        const nextMonth = addMonth(today, 1);
        const endDate = lastDayOfMonth(nextMonth);
        dispatch(getDemandDaysAction(selectedOrigin, selectedDestination, startDate, endDate));
    }

    const handleEdit = (record) => {        
        setEditingRecord({...record});
        setEditDescription(record.description || '');
        setIsModalVisible(true);
    }

    const handleModalOk = async () => {
        try {
            const response = await updateDemandDay(editingRecord.id, editDescription);            
            if (response.Status == "SUCCESS") {
                message.success('Demand day updated successfully');
                setIsModalVisible(false);
                setEditingRecord(null);
                setEditDescription('');
                // Refresh the data
                fetchDemandDays();
            } else {
                message.error(response.message || 'Failed to update demand day');
            }
        } catch (error) {
            message.error('Error updating demand day');
            console.error('Update error:', error);
        }
    }

    const handleModalCancel = () => {
        setIsModalVisible(false);
        setEditingRecord(null);
        setEditDescription('');
    }
    

    return (
        <div>
            <div style= {{marginLeft: '15%'}}>
                <div style= {{flexDirection: 'row', }}>
                    <Text strong style= {{color: '#1558B4'}}>Orign:</Text>
                    <Select
                        mode="multiple"
                        allowClear
                        showSearch
                        virtual
                        filterOption={(input, option) =>
                            option.label.toLowerCase().includes(input.toLowerCase())
                        }
                        style={{
                            marginLeft: 55,
                            width: 400
                        }}
                        placeholder="Please select Origin Cities"
                        onChange={(value)=> handleSelect(value,'origin')}
                        options={cityOptions}
                    />
                </div>
                <div style= {{flexDirection: 'row', marginTop: 5}}>
                    <Text strong style= {{color: '#1558B4'}}>Destination:</Text>
                    <Select
                        mode="multiple"
                        allowClear
                        showSearch
                        virtual
                        filterOption={(input, option) =>
                            option.label.toLowerCase().includes(input.toLowerCase())
                        }
                        style={{
                            marginLeft: 15,
                            width: 400
                        }}
                        placeholder="Please select Destination Cities"
                        onChange={(value)=> handleSelect(value,'destination')}
                        options={cityOptions}
                    />
                </div>
                <div>
                    <Button type="primary" shape="round" disabled= {isDisabled} style= {{marginTop:5, marginLeft:'10%'}} onClick= {()=> fetchDemandDays()}>
                        Search Demand days
                    </Button>
                </div>

            </div>
                <div style= {{marginTop: 10}}>
                    <ScheduleListCalender 
                        selectedDate = {scheduleDate}
                        demandDaysData = {demandDaysData.demandDaysData}
                        selectedOrigin = {selectedOrigin}
                        selectedDestination = {selectedDestination}
                    />
                    <Divider style= {{borderColor: '#817D7D'}}/>
                    <DemandDaysTable 
                        data = {demandDaysData.demandDaysData}
                        onEdit = {handleEdit}
                    />
                </div>

                <Modal
                    title="Edit Demand Day"
                    visible={isModalVisible}
                    onOk={handleModalOk}
                    onCancel={handleModalCancel}
                    okText="Update"
                    cancelText="Cancel"
                >
                    {editingRecord && (
                        <div>
                            <div style={{marginBottom: 10}}>
                                <Text strong>Origin: </Text>
                                <Text>{editingRecord.origin_city}</Text>
                            </div>
                            <div style={{marginBottom: 10}}>
                                <Text strong>Destination: </Text>
                                <Text>{editingRecord.destination_city}</Text>
                            </div>
                            <div style={{marginBottom: 10}}>
                                <Text strong>Date: </Text>
                                <Text>{editingRecord.demand_date}</Text>
                            </div>
                            <div style={{marginBottom: 10}}>
                                <Text strong>Description:</Text>
                                <TextArea
                                    rows={4}
                                    value={editDescription}
                                    onChange={(e) => setEditDescription(e.target.value)}
                                    placeholder="Enter description"
                                    style={{marginTop: 5}}
                                />
                            </div>
                        </div>
                    )}
                </Modal>
        </div>
    )
}

export default DemandDaysRoot